// src/widgets/UserByStatusWidget.jsx
//
// Widget: jumlah radius user dipecah per status (active, inactive, suspended, expired, pre_active)
// Ditampilkan sebagai donut chart, mengikuti gaya RadiusServerHealthWidget.
//
// Props:
//   filter  {object}  - { radius_server_id, tenant_id }

import { Users } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import Empty from './Empty';
import useWidgetData from '../hooks/useWidgetData';

const STATUS_COLORS = {
  active:     'var(--accent-green, #22c55e)',
  inactive:   'var(--text-muted, #94a3b8)',
  suspended:  'var(--accent-orange, #f59e0b)',
  expired:    'var(--accent-red, #ef4444)',
  pre_active: 'var(--accent-blue, #3b82f6)',
};

const STATUS_LABEL = {
  active:     'Active',
  inactive:   'Inactive',
  suspended:  'Suspended',
  expired:    'Expired',
  pre_active: 'Pre-Active',
};

const ORDER = ['active', 'pre_active', 'inactive', 'suspended', 'expired'];

export default function UserByStatusWidget({ filter = {} }) {
  const { data, loading, error } = useWidgetData('radius_user_by_status', filter);

  const total    = data?.total || 0;
  const byStatus = data?.by_status || {};
  const items    = ORDER.filter(s => byStatus[s]).map(status => ({
    status,
    label: STATUS_LABEL[status] || status,
    value: byStatus[status],
    color: STATUS_COLORS[status] || 'var(--border)',
  }));

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '16px 18px', boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
        marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
      }}>
        <Users size={13} color="var(--accent-blue)" />
        Radius User by Status
      </div>

      {loading ? (
        <div className="skeleton" style={{ flex: 1 }} />
      ) : error ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-red)', fontSize: 12, textAlign: 'center' }}>
          {error}
        </div>
      ) : items.length === 0 ? (
        <Empty message="Belum ada radius user" />
      ) : (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ position: 'relative', width: '55%', height: '100%', minWidth: 110 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={items} dataKey="value" nameKey="label"
                  cx="50%" cy="50%" innerRadius="62%" outerRadius="92%"
                  paddingAngle={items.length > 1 ? 2 : 0}
                  stroke="var(--bg-card)" strokeWidth={2}
                >
                  {items.map((it, i) => <Cell key={i} fill={it.color} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11 }}
                  formatter={(value, name) => [`${value} user`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Total user ditumpuk di tengah lubang donut */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              textAlign: 'center', pointerEvents: 'none',
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                {total}
              </div>
              <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                User
              </div>
            </div>
          </div>

          {/* Legend */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, overflowY: 'auto' }}>
            {items.map(({ status, label, value, color }) => {
              const pct = total > 0 ? ((value / total) * 100).toFixed(0) : '0';
              return (
                <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, background: color }} />
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {label}
                  </span>
                  <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', fontWeight: 600 }}>
                    {value}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 30, textAlign: 'right' }}>
                    {pct}%
                  </span>
                </div>
              );
            })}
            {/* Status dengan jumlah 0 (ditampilkan lebih redup) */}
            {ORDER.filter(s => !byStatus[s]).map(s => (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 0.35 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, background: STATUS_COLORS[s] }} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>{STATUS_LABEL[s] || s}</span>
                <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>0</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
